// src/components/SubscriptionGate.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { fmtDate } from '../utils/api';
import { IcAlert, IcLock } from './Icons';

export default function SubscriptionGate({ children, pageName }) {
  const navigate = useNavigate();
  const { currentUser, isStudent } = useAuth();
  const [subStatus, setSubStatus] = useState('loading'); // loading | active | grace | expired | none
  const [daysLeft, setDaysLeft]   = useState(0);
  const [endDate, setEndDate]     = useState(null);

  // 200L/300L students are on the free peer-led track — never gated.
  const isPeerLed = isStudent && ['200L', '300L'].includes(currentUser?.level);

  useEffect(() => {
    // Only gate paying students (undergrads outside the peer-led levels)
    if (!isStudent || isPeerLed) { setSubStatus('active'); return; }

    api.get('/payments/subscription/status').then(r => {
      const { status = 'none', daysLeft: dl = 0, endDate: ed = null } = r.data || {};
      setSubStatus(status);
      setDaysLeft(dl);
      setEndDate(ed);
    }).catch(() => setSubStatus('none'));
  }, [isStudent, isPeerLed]);

  if (subStatus === 'loading') return (
    <div className="spinner-wrap" style={{ minHeight: 300 }}><div className="spinner" /></div>
  );

  if (subStatus === 'active') {
    return (
      <>
        {/* Grace period reminder even if active — show if within 7 days of expiry */}
        {endDate && (() => {
          const daysToExpiry = Math.ceil((new Date(endDate) - Date.now()) / 86400000);
          return daysToExpiry <= 7 && daysToExpiry > 0 ? (
            <div style={{ background: 'var(--warn-l)', border: '1px solid rgba(214,137,16,0.3)', borderRadius: 'var(--radius)', padding: '10px 16px', marginBottom: 16, fontSize: 13, fontWeight: 600, color: 'var(--warn)' }}>
              <IcAlert size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />Your subscription expires in {daysToExpiry} day{daysToExpiry !== 1 ? 's' : ''} ({fmtDate(endDate)}).
              {' '}<button style={{ background: 'none', border: 'none', color: 'var(--org)', fontWeight: 700, cursor: 'pointer', fontSize: 13 }} onClick={() => navigate('/subscription')}>Renew now</button>
            </div>
          ) : null;
        })()}
        {children}
      </>
    );
  }

  if (subStatus === 'grace') {
    return (
      <>
        <div style={{ background: 'var(--warn-l)', border: '1px solid rgba(214,137,16,0.35)', borderRadius: 'var(--radius-md)', padding: '14px 18px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ fontWeight: 700, color: 'var(--warn)', marginBottom: 2 }}>
              <IcAlert size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />Grace Period — {daysLeft} day{daysLeft !== 1 ? 's' : ''} left
            </div>
            <div style={{ fontSize: 12, color: 'var(--txt-m)' }}>
              Your subscription expired on {fmtDate(endDate)}. You can view content but cannot submit assignments or take quizzes.
            </div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/subscription')}>
            Renew Subscription
          </button>
        </div>
        {/* Show content but disable interactive elements — children handle this via isGrace prop */}
        {children}
      </>
    );
  }

  // Fully expired or never subscribed
  return (
    <div className="sub-gate">
      <div className="sub-gate-icon"><IcLock size={48} /></div>
      <div className="sub-gate-title">
        {subStatus === 'expired' ? 'Subscription Expired' : `${pageName || 'This Page'} is Locked`}
      </div>
      <div className="sub-gate-sub">
        {subStatus === 'expired'
          ? `Your subscription expired on ${fmtDate(endDate)}. Renew to regain full access to your courses, assignments, quizzes, and ID card.`
          : 'Subscribe to unlock assignments, quizzes, past questions, timetable, certificates, and your digital ID card.'}
      </div>
      {subStatus === 'expired' && (
        <div className="sub-gate-grace" style={{ background: 'var(--err-l)', border: '1px solid rgba(192,57,43,0.2)', color: 'var(--err)' }}>
          Your 7-day grace period has ended. Full access is now locked.
        </div>
      )}
      <button className="btn btn-primary btn-lg btn-full" onClick={() => navigate('/subscription')}>
        View Subscription Plans
      </button>
      <button className="btn btn-ghost btn-md btn-full mt-8" onClick={() => navigate('/feed')}>
        Go to News Feed (Free)
      </button>
    </div>
  );
}
